import { useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Plus, Trash2, Car, ChevronDown, ChevronUp, FileSearch, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

// 🆕 Feb 2026 — Tipi veicolo consentiti (allineati alla lista già in uso in Policies/Wizard).
export const VEHICLE_TYPES = [
  { v: "auto", l: "Auto" },
  { v: "moto", l: "Moto" },
  { v: "autocarro", l: "Autocarro" },
  { v: "veicolo", l: "Veicolo (generico)" },
  { v: "nautica", l: "Nautica" },
];

const _labelForType = (t) => (VEHICLE_TYPES.find((x) => x.v === t)?.l) || (t || "—");

/**
 * Editor per veicoli AGGIUNTIVI di una polizza multi-veicolo.
 * Il veicolo PRINCIPALE resta gestito dai campi top-level `vehicle_*` del form.
 * Questo componente gestisce solo `vehicles[1..N]` (dal 2° veicolo in poi).
 *
 * Props:
 * - extraVehicles: array di veicoli
 * - onChange: (newArray) => void
 * - primaryPlate: targa del veicolo principale (per validare unicità)
 * - disabled: readonly
 */
export function VehiclesEditor({ extraVehicles = [], onChange, primaryPlate = "", disabled = false }) {
  const vehicles = extraVehicles || [];
  // Stato collapse per riga (default: espansa quando appena aggiunta, altrimenti compressa)
  const [collapsedMap, setCollapsedMap] = useState({});
  const isCollapsed = (i) => collapsedMap[i] === true;
  const toggle = (i) => setCollapsedMap((m) => ({ ...m, [i]: !m[i] }));
  // 🆕 OCR libretto → nuovo veicolo
  const [ocrLoading, setOcrLoading] = useState(false);
  const fileInputRef = useRef(null);
  const _defaultTypeFromCategory = (cat) => {
    const c = (cat || "").toUpperCase();
    if (c.startsWith("L")) return "moto"; // L1e..L7e = motocicli/ciclomotori
    if (c.startsWith("N")) return "autocarro"; // N1/N2/N3 = veicoli merci
    if (c.startsWith("M")) return "auto"; // M1/M2/M3 = passeggeri
    return "auto";
  };
  const handleLibrettoFile = async (file) => {
    if (!file || disabled) return;
    setOcrLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/utils/extract-libretto", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const ex = data?.extracted || {};
      const newVehicle = {
        plate: (ex.vehicle_plate || "").toUpperCase().replace(/\s+/g, ""),
        type: ex.vehicle_type || _defaultTypeFromCategory(ex.category),
        brand: ex.vehicle_brand || "",
        model: ex.vehicle_model || "",
        year: ex.vehicle_year || "",
        category: ex.category || "",
        notes: "",
        bollo_due_date: ex.bollo_due_date || "",
        bollo_amount: ex.bollo_amount ?? "",
        last_review_date: ex.last_review_date || "",
        next_review_date: ex.next_review_date || "",
      };
      onChange([...vehicles, newVehicle]);
      setCollapsedMap((m) => ({ ...m, [vehicles.length]: false }));
      toast.success(`Veicolo estratto dal libretto${newVehicle.plate ? ` (targa ${newVehicle.plate})` : ""} — verifica i campi`);
    } catch (e) {
      toast.error(formatApiError(e) || "Errore estrazione libretto");
    } finally {
      setOcrLoading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const dupPlateIndexes = useMemo(() => {
    const plates = vehicles.map((v) => (v?.plate || "").trim().toUpperCase());
    const seen = {};
    const dup = new Set();
    const primaryUp = (primaryPlate || "").trim().toUpperCase();
    plates.forEach((p, i) => {
      if (!p) return;
      if (p === primaryUp) dup.add(i);
      if (seen[p] !== undefined) {
        dup.add(seen[p]);
        dup.add(i);
      } else {
        seen[p] = i;
      }
    });
    return dup;
  }, [vehicles, primaryPlate]);

  const update = (i, patch) => {
    const next = vehicles.map((v, idx) => (idx === i ? { ...v, ...patch } : v));
    onChange(next);
  };

  const add = () => {
    onChange([...vehicles, { plate: "", type: "", brand: "", model: "", year: "", category: "", notes: "", bollo_due_date: "", bollo_amount: "", last_review_date: "", next_review_date: "" }]);
    // Nuovo veicolo → espanso di default
    setCollapsedMap((m) => ({ ...m, [vehicles.length]: false }));
  };

  const remove = (i) => {
    if (!window.confirm("Rimuovere questo veicolo dalla polizza?")) return;
    const next = vehicles.filter((_, idx) => idx !== i);
    onChange(next);
    // Ri-normalizza collapsedMap
    const nextMap = {};
    Object.entries(collapsedMap).forEach(([k, v]) => {
      const ki = parseInt(k, 10);
      if (ki < i) nextMap[ki] = v;
      else if (ki > i) nextMap[ki - 1] = v;
    });
    setCollapsedMap(nextMap);
  };

  return (
    <div className="space-y-3" data-testid="vehicles-editor">
      <div className="flex items-center justify-between">
        <div className="text-sm font-medium text-slate-700 flex items-center gap-2">
          <Car className="h-4 w-4 text-sky-600" />
          Veicoli aggiuntivi
          {vehicles.length > 0 && <span className="text-xs text-slate-500">({vehicles.length})</span>}
        </div>
        <Button type="button" size="sm" variant="outline" onClick={add} disabled={disabled} data-testid="add-vehicle-btn">
          <Plus className="h-3 w-3 mr-1" /> Aggiungi veicolo
        </Button>
      </div>
      {/* 🆕 OCR libretto → nuovo veicolo aggiuntivo */}
      <div className="flex items-center gap-2 -mt-1">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,.pdf"
          className="hidden"
          onChange={(e) => handleLibrettoFile(e.target.files?.[0])}
          data-testid="libretto-file-input"
        />
        <Button
          type="button"
          size="sm"
          variant="secondary"
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled || ocrLoading}
          className="text-[11px] h-7 bg-sky-50 hover:bg-sky-100 text-sky-800 border border-sky-200"
          data-testid="add-vehicle-from-libretto-btn"
          title="Carica un libretto di circolazione: il sistema estrarrà i dati e creerà un nuovo veicolo pre-compilato"
        >
          {ocrLoading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <FileSearch className="h-3 w-3 mr-1" />}
          {ocrLoading ? "Analisi in corso…" : "📎 Aggiungi da libretto (AI OCR)"}
        </Button>
      </div>

      {vehicles.length === 0 ? (
        <div className="text-xs text-slate-500 italic p-3 bg-slate-50 rounded-md border border-dashed border-slate-200">
          Nessun veicolo aggiuntivo. Il veicolo principale è già gestito nei campi sopra.
          Clicca <strong>Aggiungi veicolo</strong> per polizze multi-veicolo (flotte, familiari, ecc.).
        </div>
      ) : (
        vehicles.map((v, i) => {
          const isDup = dupPlateIndexes.has(i);
          const collapsed = isCollapsed(i);
          return (
            <div key={i} className={`rounded-md border ${isDup ? "border-red-300 bg-red-50/40" : "border-slate-200 bg-slate-50/40"}`} data-testid={`vehicle-row-${i}`}>
              {/* Header cliccabile per collapse/expand */}
              <div className="flex items-center justify-between px-3 py-2 cursor-pointer select-none" onClick={() => toggle(i)} data-testid={`vehicle-toggle-${i}`}>
                <div className="flex items-center gap-2 text-xs">
                  {collapsed ? <ChevronDown className="h-3.5 w-3.5 text-slate-500" /> : <ChevronUp className="h-3.5 w-3.5 text-slate-500" />}
                  <span className="font-semibold text-slate-700">Veicolo #{i + 2}</span>
                  {v.plate ? <span className="font-mono font-bold text-sky-700">{v.plate}</span> : <span className="text-slate-400 italic">senza targa</span>}
                  {v.type && <span className="px-1.5 py-0.5 rounded bg-sky-100 text-sky-700 text-[10px] font-semibold">{_labelForType(v.type)}</span>}
                  {(v.brand || v.model) && <span className="text-slate-500">· {[v.brand, v.model].filter(Boolean).join(" ")}</span>}
                  {isDup && <span className="text-red-600 text-[10px] font-semibold">⚠ Targa duplicata</span>}
                </div>
                <Button type="button" size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); remove(i); }} disabled={disabled} className="text-red-600 hover:bg-red-50 h-7" data-testid={`remove-vehicle-${i}`}>
                  <Trash2 className="h-3 w-3 mr-1" /> Rimuovi
                </Button>
              </div>

              {/* Body espandibile */}
              {!collapsed && (
                <div className="px-3 pb-3 space-y-2 border-t border-slate-200">
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 pt-2">
                    <div>
                      <Label className="text-[11px]">Targa *</Label>
                      <Input
                        value={v.plate || ""}
                        onChange={(e) => update(i, { plate: e.target.value.replace(/\s+/g, "").toUpperCase() })}
                        disabled={disabled}
                        placeholder="AB123CD"
                        className={`font-mono ${isDup ? "border-red-400" : ""}`}
                        data-testid={`vehicle-plate-${i}`}
                      />
                      {isDup && <div className="text-[10px] text-red-600 mt-0.5">Targa duplicata nella polizza</div>}
                    </div>
                    <div>
                      <Label className="text-[11px]">Tipo veicolo *</Label>
                      <Select value={v.type || ""} onValueChange={(val) => update(i, { type: val })} disabled={disabled}>
                        <SelectTrigger data-testid={`vehicle-type-${i}`}>
                          <SelectValue placeholder="Seleziona tipo…" />
                        </SelectTrigger>
                        <SelectContent>
                          {VEHICLE_TYPES.map((t) => (
                            <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-[11px]">Marca</Label>
                      <Input value={v.brand || ""} onChange={(e) => update(i, { brand: e.target.value })} disabled={disabled} data-testid={`vehicle-brand-${i}`} />
                    </div>
                    <div>
                      <Label className="text-[11px]">Modello</Label>
                      <Input value={v.model || ""} onChange={(e) => update(i, { model: e.target.value })} disabled={disabled} data-testid={`vehicle-model-${i}`} />
                    </div>
                    <div>
                      <Label className="text-[11px]">Anno</Label>
                      <Input type="number" value={v.year || ""} onChange={(e) => update(i, { year: e.target.value })} disabled={disabled} placeholder="2024" min={1900} max={2100} data-testid={`vehicle-year-${i}`} />
                    </div>
                    <div>
                      <Label className="text-[11px]">Categoria (M1/N1/L3e…)</Label>
                      <Input value={v.category || ""} onChange={(e) => update(i, { category: e.target.value })} disabled={disabled} placeholder="M1 / N1 / L3e…" data-testid={`vehicle-category-${i}`} />
                    </div>
                    <div>
                      <Label className="text-[11px]">Bollo scadenza</Label>
                      <Input type="date" value={v.bollo_due_date || ""} onChange={(e) => update(i, { bollo_due_date: e.target.value })} disabled={disabled} data-testid={`vehicle-bollo-date-${i}`} />
                    </div>
                    <div>
                      <Label className="text-[11px]">Bollo importo (€)</Label>
                      <Input type="number" step="0.01" value={v.bollo_amount ?? ""} onChange={(e) => update(i, { bollo_amount: e.target.value === "" ? "" : Number(e.target.value) })} disabled={disabled} data-testid={`vehicle-bollo-amount-${i}`} />
                    </div>
                    <div>
                      <Label className="text-[11px]">Ultima revisione</Label>
                      <Input type="date" value={v.last_review_date || ""} onChange={(e) => update(i, { last_review_date: e.target.value })} disabled={disabled} data-testid={`vehicle-last-review-${i}`} />
                    </div>
                    <div>
                      <Label className="text-[11px]">Prossima revisione</Label>
                      <Input type="date" value={v.next_review_date || ""} onChange={(e) => update(i, { next_review_date: e.target.value })} disabled={disabled} data-testid={`vehicle-next-review-${i}`} />
                    </div>
                  </div>
                  <div>
                    <Label className="text-[11px]">Note veicolo</Label>
                    <Textarea value={v.notes || ""} onChange={(e) => update(i, { notes: e.target.value })} disabled={disabled} rows={2} placeholder="Note specifiche (colore, telaio, note d'uso…)" data-testid={`vehicle-notes-${i}`} />
                  </div>
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
}

/**
 * Utility: unisce il veicolo principale (dai campi top-level del form) con gli extra
 * per costruire il payload `vehicles[]` da inviare al backend.
 * Ritorna undefined se non c'è nemmeno un veicolo principale con targa (polizza non-auto).
 */
export function buildVehiclesPayload(form) {
  const primaryPlate = (form.vehicle_plate || "").trim().toUpperCase();
  const extras = (form.vehicles_extra || []).filter((v) => (v?.plate || "").trim());
  if (!primaryPlate && extras.length === 0) return undefined;
  const primary = primaryPlate ? {
    plate: primaryPlate,
    type: (form.type && ["auto", "moto", "autocarro", "veicolo", "nautica"].includes(form.type)) ? form.type : null,
    brand: form.vehicle_brand || null,
    model: form.vehicle_model || null,
    year: null,
    category: form.vehicle_category || null,
    notes: null,
    bollo_due_date: form.bollo_due_date || null,
    bollo_amount: form.bollo_amount ?? null,
    last_review_date: form.last_review_date || null,
    next_review_date: form.next_review_date || null,
  } : null;
  const clean = (v) => ({
    plate: (v.plate || "").trim().toUpperCase(),
    type: (v.type && ["auto", "moto", "autocarro", "veicolo", "nautica"].includes(v.type)) ? v.type : null,
    brand: v.brand || null,
    model: v.model || null,
    year: v.year ? Number(v.year) : null,
    category: v.category || null,
    notes: v.notes || null,
    bollo_due_date: v.bollo_due_date || null,
    bollo_amount: v.bollo_amount === "" || v.bollo_amount == null ? null : Number(v.bollo_amount),
    last_review_date: v.last_review_date || null,
    next_review_date: v.next_review_date || null,
  });
  return [
    ...(primary ? [primary] : []),
    ...extras.map(clean),
  ];
}
